import { useState, useEffect, useCallback } from "react";

export type HealthStatus = "loading" | "healthy" | "unhealthy";

export interface ServiceHealthResult {
    status: HealthStatus;
    message: string;
}

export interface ServiceHealthConfig {
    name: string;
    url: string;
}

/**
 * Fetches the health status of a service endpoint.
 * Expects the endpoint to return JSON with a `message` field.
 */
export function useServiceHealth(
    config: ServiceHealthConfig,
    { interval = 30_000 }: { interval?: number } = {},
): ServiceHealthResult {
    const [
        result,
        setResult,
    ] = useState<ServiceHealthResult>({
        status: "loading",
        message: "",
    });

    const check = useCallback(async () => {
        try {
            const response = await fetch(config.url, {
                method: "GET",
                headers: { Accept: "application/json" },
            });

            if (response.ok) {
                const data = (await response.json()) as { message?: string };
                setResult({
                    status: "healthy",
                    message: data.message ?? `${config.name} healthy`,
                });
            } else {
                setResult({
                    status: "unhealthy",
                    message: `${config.name} returned ${response.status}`,
                });
            }
        } catch {
            setResult({
                status: "unhealthy",
                message: `${config.name} unreachable`,
            });
        }
    }, [
        config.url,
        config.name,
    ]);

    useEffect(() => {
        check();
        const timer = setInterval(check, interval);
        return () => clearInterval(timer);
    }, [
        check,
        interval,
    ]);

    return result;
}
